import { useHasPermission } from '@contexts/permissions-provider';
import { useDashboardAlerts } from '@features/home/hooks/use-dashboard-alerts/useDashboardAlerts';
import { usePastDueInvoiceAlerts } from '@features/invoices/hooks/use-past-due-invoice-alerts/usePastDueInvoiceAlerts';
import * as React from 'react';

import type { UseInvoiceAlertStateReturn } from './useInvoiceAlertState.types';

export function useInvoiceAlertState(options?: {
  enabled?: boolean;
}): UseInvoiceAlertStateReturn {
  const isAlertsFeatureEnabled = options?.enabled ?? true;
  const canViewInvoices = useHasPermission('Invoices_View_Org');
  const {
    data: dashboardAlerts = [],
    isLoading: invoiceIsLoading,
    error: invoiceError,
  } = useDashboardAlerts({ enabled: isAlertsFeatureEnabled && canViewInvoices });

  const [isPanelOpen, setIsPanelOpen] = React.useState<boolean>(false);
  const [selectedInvoiceId, setSelectedInvoiceId] = React.useState<
    string | null
  >(null);

  const {
    data: pastDueInvoices = [],
    isLoading: pastDueIsLoading,
    error: pastDueError,
  } = usePastDueInvoiceAlerts({
    enabled: isAlertsFeatureEnabled && isPanelOpen,
  });

  const pastDueAlert = React.useMemo(
    () => dashboardAlerts.find(alert => alert.type === 'PastDueInvoices'),
    [dashboardAlerts]
  );

  const hasInvoiceAlerts =
    isAlertsFeatureEnabled &&
    canViewInvoices &&
    !!pastDueAlert &&
    pastDueAlert.count > 0;

  const openPanel = React.useCallback((): void => {
    setSelectedInvoiceId(null);
    setIsPanelOpen(true);
  }, []);

  const closePanel = React.useCallback((): void => {
    setSelectedInvoiceId(null);
    setIsPanelOpen(false);
  }, []);

  const handleInvoiceClick = React.useCallback(
    (accountReceivableId: string): void => {
      setIsPanelOpen(false);
      setSelectedInvoiceId(accountReceivableId);
    },
    []
  );

  const handleDetailClose = React.useCallback((): void => {
    setSelectedInvoiceId(null);
    setIsPanelOpen(true);
  }, []);

  return {
    hasInvoiceAlerts,
    pastDueCount: pastDueAlert?.count ?? 0,
    pastDueInvoices,
    pastDueIsLoading,
    pastDueError: pastDueError instanceof Error ? pastDueError : null,
    invoiceIsLoading,
    invoiceError: invoiceError instanceof Error ? invoiceError : null,
    isPanelOpen,
    selectedInvoiceId,
    openPanel,
    closePanel,
    handleInvoiceClick,
    handleDetailClose,
  };
}
